import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LegalDocViewer from "@/components/LegalDocViewer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScrollText } from "lucide-react";

export default function LegalAcceptancePage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [checks, setChecks] = useState({ tos: false, dpa: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/legal/documents").then(({ data }) => setDocs(data)).catch(() => setDocs([]));
  }, []);

  const alreadyAccepted = Boolean(user && user.role === "admin" && user.legal_accepted);
  useEffect(() => {
    if (alreadyAccepted) navigate("/admin", { replace: true });
  }, [alreadyAccepted, navigate]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/admin/legal/accept");
      setUser({ ...user, legal_accepted: true });
      toast.success("Termini di Servizio e DPA accettati");
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const docRow = (type, label) => {
    const d = (docs || []).find((x) => x.doc_type === type);
    if (!d) return null;
    return (
      <div className="border border-slate-200 rounded-xl p-4 sm:p-5" data-testid={`legal-accept-${type}`}>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-slate-900">{d.title}</p>
          <Badge className="bg-amber-100 text-amber-800">Versione {d.version} — provvisoria</Badge>
        </div>
        <LegalDocViewer doc={d} label="Leggi il documento completo" testid={`legal-read-${type}`} />
        <div className="mt-3 flex items-start gap-2">
          <Checkbox data-testid={`legal-check-${type}`} id={`check-${type}`} checked={checks[type]}
            onCheckedChange={(v) => setChecks({ ...checks, [type]: Boolean(v) })} />
          <label htmlFor={`check-${type}`} className="text-sm text-slate-700 leading-snug cursor-pointer">
            Dichiaro di aver letto e di accettare integralmente {label} (versione {d.version}).
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-2xl institutional-card" data-testid="legal-acceptance-page">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <ScrollText className="h-5 w-5 text-[#003366]" />
            <h1 className="font-display text-xl font-bold text-slate-900">Accettazione documenti contrattuali</h1>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Prima di accedere al pannello dell'istituto è necessario leggere e accettare i Termini di Servizio e
            l'Accordo sul Trattamento dei Dati (DPA) di ITS Match. Entrambe le accettazioni sono obbligatorie e
            verranno registrate con data, ora e versione del documento.
          </p>
          {docs === null ? (
            <p className="text-sm text-slate-500">Caricamento documenti...</p>
          ) : (
            <div className="space-y-4">
              {docRow("tos", "i Termini di Servizio di ITS Match")}
              {docRow("dpa", "l'Accordo sul Trattamento dei Dati Personali (DPA)")}
            </div>
          )}
          <Button data-testid="legal-accept-submit" className="w-full mt-6 bg-[#003366] hover:bg-[#002244]"
            disabled={!checks.tos || !checks.dpa || busy} onClick={accept}>
            {busy ? "Registrazione in corso..." : "Accetta e accedi al pannello"}
          </Button>
          <p data-testid="legal-accept-hint" className="mt-3 text-xs text-slate-500 text-center">
            Il pulsante si attiva solo dopo aver spuntato entrambe le caselle di accettazione.
          </p>
          <button type="button" data-testid="legal-accept-logout"
            className="mt-4 text-xs text-slate-500 hover:text-slate-800 w-full text-center"
            onClick={async () => { await logout(); navigate("/auth"); }}>
            Esci e torna al login
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
